import { useState, useEffect } from "react";
import { ScrollFade } from "@/components/ScrollFade";
import api from "@/lib/api";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks,
  format, isSameMonth, isSameDay, parseISO,
} from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

const dayNames = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

export default function CalendarPage() {
  const [posts, setPosts] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => { api.get("/posts").then((r) => setPosts(r.data)); }, []);

  const dateOf = (p) => {
    const s = p.scheduled_at || p.published_at;
    return s ? parseISO(s) : null;
  };
  const postsForDay = (day) =>
    posts.filter((p) => {
      const d = dateOf(p);
      return d && isSameDay(d, day) && (p.status === "scheduled" || p.status === "published");
    });

  let days = [];
  if (view === "month") {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
  } else {
    const start = startOfWeek(cursor, { weekStartsOn: 1 });
    for (let i = 0; i < 7; i++) days.push(addDays(start, i));
  }

  const navigate = (dir) => {
    if (view === "month") setCursor(addMonths(cursor, dir));
    else setCursor(addWeeks(cursor, dir));
  };

  const title = view === "month"
    ? format(cursor, "MMMM yyyy", { locale: fr })
    : `Semaine du ${format(startOfWeek(cursor, { weekStartsOn: 1 }), "d MMM", { locale: fr })}`;

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <CalendarDays className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Planification</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-serif text-4xl text-slate-900 capitalize">{title}</h1>
        <div className="flex items-center gap-3">
          <div className="flex border border-slate-200 rounded-sm overflow-hidden">
            <button data-testid="view-week" onClick={() => setView("week")}
              className={`px-4 py-2 text-sm font-medium transition-colors ${view === "week" ? "bg-coral-strong text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              Semaine
            </button>
            <button data-testid="view-month" onClick={() => setView("month")}
              className={`px-4 py-2 text-sm font-medium transition-colors ${view === "month" ? "bg-coral-strong text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              Mois
            </button>
          </div>
          <div className="flex gap-1">
            <button data-testid="cal-prev" onClick={() => navigate(-1)} className="p-2 border border-slate-200 rounded-sm hover:border-coral transition-colors">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button data-testid="cal-next" onClick={() => navigate(1)} className="p-2 border border-slate-200 rounded-sm hover:border-coral transition-colors">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <ScrollFade data-testid="calendar-scroll">
      <div className="grid grid-cols-7 gap-px bg-slate-200 border border-slate-200 rounded-sm overflow-hidden min-w-[720px] lg:min-w-0">
        {dayNames.map((d) => (
          <div key={d} className="bg-secondary text-ink-light text-xs font-semibold uppercase tracking-[0.15em] py-3 text-center">{d}</div>
        ))}
        {days.map((day, i) => {
          const dayPosts = postsForDay(day);
          const muted = view === "month" && !isSameMonth(day, cursor);
          const today = isSameDay(day, new Date());
          return (
            <div key={i} data-testid={`cal-day-${format(day, "yyyy-MM-dd")}`}
              className={`bg-white min-w-0 break-words ${view === "month" ? "min-h-[120px]" : "min-h-[280px]"} p-2 ${muted ? "opacity-40" : ""}`}>
              <div className={`text-sm mb-2 ${today ? "font-bold text-coral-dark" : "text-slate-500"}`}>
                {format(day, "d")}
              </div>
              <div className="space-y-1.5">
                {dayPosts.map((p) => (
                  <div key={p.id} data-testid={`cal-post-${p.id}`}
                    className={`text-xs rounded-sm px-2 py-1.5 leading-snug ${
                      p.status === "published" ? "bg-coral-soft text-coral-dark border-l-2 border-coral" : "bg-coral-soft text-coral-dark border-l-2 border-coral"
                    }`}>
                    <span className="block font-semibold">{format(dateOf(p), "HH:mm")}</span>
                    <span className="line-clamp-2">{p.content.slice(0, 60)}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      </ScrollFade>
      <div className="flex flex-wrap items-center gap-6 mt-5 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-coral-soft border-l-2 border-coral" /> Programmé</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-coral-soft border-l-2 border-coral" /> Publié</span>
      </div>
    </div>
  );
}
